import React from 'react';
import GeneralTab from './tabs/GeneralTab';
import QuickBooksTab from './tabs/QuickBooksTab';
import AdminTab from './tabs/AdminTab';
import DataTab from './tabs/DataTab';
import NotificationsTab from './tabs/NotificationsTab';
import SecurityTab from './tabs/SecurityTab';
import AboutTab from './tabs/AboutTab';
import DocumentLibraryTab from './tabs/DocumentLibraryTab';
import settingsService from '../services/settingsService';
import ConfirmDialog from '../../ConfirmDialog';
import useConfirm from '../../../hooks/useConfirm';

/**
 * Main content area component for settings tabs
 * Routes active tab to appropriate tab component with shared state
 */
const SettingsContent = ({
  activeTab,
  userProfile,
  qbSettings,
  loading,
  onQbSettingsChange,
  showNotification
}) => {
  const isAdmin = userProfile?.role === 'admin';
  const { confirmDialog, confirm } = useConfirm();

  const handleSaveQuickBooksSettings = async () => {
    return await settingsService.saveQuickBooksSettings(
      qbSettings,
      () => {}, // setLoading handled in parent
      showNotification
    );
  };

  const handleExportClients = async () => {
    try {
      await settingsService.exportClientData();
      showNotification('Client data exported successfully!');
    } catch (error) {
      showNotification('Failed to export client data', 'error');
    }
  };

  const handleExportJobs = async () => {
    try {
      await settingsService.exportJobData();
      showNotification('Job data exported successfully!');
    } catch (error) {
      showNotification('Failed to export job data', 'error');
    }
  };

  const handleDeleteAllData = async () => {
    const ok = await confirm({
      title: 'Delete All Data',
      message: 'This will permanently delete ALL data. This action cannot be undone.',
      variant: 'danger',
      confirmLabel: 'Delete Everything'
    });
    if (!ok) return;
    try {
      await settingsService.deleteAllData();
      showNotification('All data deleted successfully!');
    } catch (error) {
      showNotification('Failed to delete data', 'error');
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return <GeneralTab userProfile={userProfile} />;

      case 'quickbooks':
        return (
          <QuickBooksTab
            isAdmin={isAdmin}
            userProfile={userProfile}
            qbSettings={qbSettings}
            onSettingsChange={onQbSettingsChange}
            onSave={handleSaveQuickBooksSettings}
            loading={loading}
            showNotification={showNotification}
          />
        );

      case 'admin':
        return (
          <AdminTab
            userProfile={userProfile}
            showNotification={showNotification}
          />
        );

      case 'data':
        return (
          <DataTab
            onExportClients={handleExportClients}
            onExportJobs={handleExportJobs}
            onDeleteAllData={handleDeleteAllData}
          />
        );

      case 'notifications':
        return <NotificationsTab />;

      case 'security':
        return <SecurityTab />;

      case 'about':
        return <AboutTab />;

      case 'document-library':
        return (
          <DocumentLibraryTab
            userProfile={userProfile}
            showNotification={showNotification}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex-1">
      <div className="bg-white rounded-lg shadow-md p-6">
        {renderTabContent()}
      </div>
      <ConfirmDialog {...confirmDialog} />
    </div>
  );
};

export default SettingsContent;
